import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, GitBranch, Pencil } from 'lucide-react'
import { AttachmentsPanel } from '@/components/crm/attachments-panel'
import { CommentsThread } from '@/components/crm/comments-thread'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { LayoutAwareDetail } from '@/components/crm/layout-aware-detail'
import { TagPicker } from '@/components/crm/tag-picker'
import { Timeline } from '@/components/crm/timeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/leads/$leadId')({
  component: LeadDetailPage,
})

type LeadRecord = {
  id: string
  firstName?: string | null
  lastName?: string | null
  companyName?: string | null
  email?: string | null
  status?: string | null
  ownerUserId?: string | null
  score?: number | null
  lastContactedAt?: string | Date | null
  _ext?: Record<string, unknown>
}

type ActivityRow = {
  id: string
  activityType?: string | null
  subject?: string | null
  dueAt?: string | Date | null
  completedAt?: string | Date | null
  ownerUserId?: string | null
}

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function LeadDetailPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const { leadId } = Route.useParams()

  const { data, isLoading } = useQuery(orpc.crm.leads.get.queryOptions({ input: { id: leadId } }))

  const { data: activityRows } = useQuery(
    orpc.crm.activities.list.queryOptions({
      input: { limit: 100, offset: 0, relatedToType: 'lead', relatedToId: leadId },
    }),
  )

  const lead = data as LeadRecord | undefined

  if (isLoading || !lead) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const fullName =
    `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() || lead.email || '(unnamed lead)'

  const activities: ActivityRow[] = (activityRows as ActivityRow[] | undefined) ?? []

  const activityColumns: EntityListColumn<ActivityRow>[] = [
    {
      key: 'activityType',
      header: t('crm.activity.type'),
      render: (r) => <span className="capitalize">{r.activityType ?? '-'}</span>,
    },
    { key: 'subject', header: t('crm.activity.subject'), render: (r) => r.subject ?? '-' },
    { key: 'dueAt', header: t('crm.activity.dueAt'), render: (r) => formatDate(r.dueAt) },
    {
      key: 'completedAt',
      header: t('crm.activity.completedAt'),
      render: (r) =>
        r.completedAt ? (
          formatDate(r.completedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/leads' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{fullName}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            {lead.status && (
              <Badge variant="outline" className="capitalize">
                {lead.status}
              </Badge>
            )}
            {typeof lead.score === 'number' && (
              <span>
                {t('crm.lead.score')}: {lead.score}
              </span>
            )}
            {lead.ownerUserId && (
              <span>
                {t('crm.common.ownedBy')}: {lead.ownerUserId}
              </span>
            )}
            {lead.lastContactedAt && (
              <span>
                {t('crm.common.updatedAt')}: {formatDate(lead.lastContactedAt)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/crm/leads/$leadId/edit', params: { leadId } })}
          >
            <Pencil className="h-4 w-4 mr-2" />
            {t('crm.common.edit')}
          </Button>
          <Button
            onClick={() => navigate({ to: '/crm/leads/$leadId/convert', params: { leadId } })}
            disabled={lead.status === 'converted'}
          >
            <GitBranch className="h-4 w-4 mr-2" />
            {t('crm.common.convert')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">{t('navigation.details')}</TabsTrigger>
          <TabsTrigger value="timeline">{t('navigation.timeline')}</TabsTrigger>
          <TabsTrigger value="activities">{t('crm.activity.entityPlural')}</TabsTrigger>
          <TabsTrigger value="tags">{t('navigation.inputTags')}</TabsTrigger>
          <TabsTrigger value="comments">{t('navigation.chat')}</TabsTrigger>
          <TabsTrigger value="attachments">{t('navigation.fileDropzone')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <LayoutAwareDetail
            entityKey="lead"
            record={lead as Record<string, unknown> & { id: string }}
          />
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline recordType="lead" recordId={leadId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.activity.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList rows={activities} columns={activityColumns} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.inputTags')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TagPicker recordType="lead" recordId={leadId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.chat')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentsThread recordType="lead" recordId={leadId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.fileDropzone')}</CardTitle>
            </CardHeader>
            <CardContent>
              <AttachmentsPanel recordType="lead" recordId={leadId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
